import type {
  IEmailIntegrationData,
  IEmailMarketingData,
  IFacebookData,
  ILinkedInData,
  ILinkedInOrganization,
  IntegrationTypes,
  ITeamsData,
  ITwitterData,
  IUserIntegration,
  IWordpressData,
  IZapierIntegrationData,
} from "@/types/account";

export function findIntegrations<C extends IntegrationTypes>(
  integrations: IUserIntegration[] = [],
  type: C,
  onlyEnabled = false
): IUserIntegration<C>[] {
  if (!integrations?.length || !type) return [];
  return integrations.filter(
    integration =>
      integration.id.startsWith(type) &&
      (onlyEnabled ? !!integration.enabled : true) &&
      !(type === "email" && integration.id.startsWith("email-marketing"))
  ) as IUserIntegration<C>[];
}

export function findLinkedInOrganizations(
  integrations: IUserIntegration[] = []
): ILinkedInOrganization[] {
  if (!integrations?.length) return [];

  // Get all enabled LinkedIn integrations
  const linkedinIntegrations = findIntegrations(integrations, "linkedin", true);

  // Extract all organizations from all LinkedIn integrations
  return linkedinIntegrations.reduce((orgs: ILinkedInOrganization[], integration) => {
    const integrationData = integration.data as IUserIntegration<"linkedin">["data"];
    const organizations = integrationData?.organizations || [];
    return [...orgs, ...organizations];
  }, []);
}

export function usePostIntegrationCheck() {
  const { user } = useCurrentUser();

  function canPostToPlatform(platform: IntegrationTypes, feature: string) {
    const integrations = findIntegrations(user.value?.integrations || [], platform, true);
    return (
      !!integrations.length &&
      !!integrations.some(i => i.enabled) &&
      integrations.some(i => i.features.some(v => v.id === feature && v.enabled))
    );
  }

  function hasPlatformIntegration(platform: IntegrationTypes) {
    return !!findIntegrations(user.value?.integrations || [], platform, true).some(
      i => !!i.enabled
    );
  }

  const hasFacebookIntegration = computed(() => hasPlatformIntegration("facebook"));

  const hasInstagramIntegration = computed(() => hasPlatformIntegration("instagram"));

  const hasTwitterIntegration = computed(() => hasPlatformIntegration("twitter"));

  const hasLinkedInIntegration = computed(() => hasPlatformIntegration("linkedin"));

  const hasWordpressIntegration = computed(() => hasPlatformIntegration("wordpress"));

  const hasTikTokIntegration = computed(() => hasPlatformIntegration("tiktok"));

  const hasYouTubeIntegration = computed(() => hasPlatformIntegration("youtube"));

  const canPostToFacebook = computed(() =>
    canPostToPlatform("facebook", "pages_manage_posts")
  );

  const canPostToInstagram = computed(
    () =>
      canPostToPlatform("instagram", "instagram_business_content_publish") ||
      canPostToPlatform("instagram", "instagram_content_publish") ||
      canPostToPlatform("instagram", "instagram_business_basic") ||
      canPostToPlatform("instagram", "instagram_business_content_publishing")
  );

  const canPostToTwitter = computed(() => canPostToPlatform("twitter", "tweet.write"));

  const canPostToLinkedIn = computed(() =>
    canPostToPlatform("linkedin", "w_member_social")
  );

  function getIntegrationProfileName(platform: IntegrationTypes, id: string): string {
    if (!platform) return "";
    const integrationData = findIntegrations(
      user.value?.integrations || [],
      platform,
      true
    )?.find(i => i.id === id)?.data;
    if (!integrationData) return "";
    switch (platform) {
      case "twitter":
        return (integrationData as ITwitterData)?.profile?.name || "";
      case "instagram":
      case "facebook":
        return (
          (integrationData as IFacebookData).pages?.find(
            (p: IFacebookData["pages"][0]) =>
              p.pageId === (integrationData as IFacebookData).pageId
          )?.name || ""
        );
      case "linkedin":
        return (integrationData as ILinkedInData).profile?.name || "";
      case "wordpress":
        return (integrationData as IWordpressData).profile?.username || "";
      case "email-marketing":
        return (integrationData as IEmailMarketingData).emailAddress || "";
      case "email":
        return (
          (integrationData as IEmailIntegrationData).targetEmail ||
          (integrationData as IEmailIntegrationData).emailAddress ||
          ""
        );
      case "zapier":
        return (
          ((integrationData as IZapierIntegrationData).webHooks?.length || 0) +
          " webhooks"
        );
      case "teams":
        return (integrationData as ITeamsData).outgoingUrl || "";
      // case "google-analytics":

      default:
        return "";
    }
  }

  const canPostToWordpress = computed(() =>
    canPostToPlatform("wordpress", "post_articles")
  );

  const canPostToTikTok = computed(() => hasPlatformIntegration("tiktok"));

  const canPostToYouTube = computed(() => hasPlatformIntegration("youtube"));

  function getProfileNames(platform: IntegrationTypes, ids: string[]): string[] {
    if (!platform || !ids?.length) return [];
    return ids.map(id => getIntegrationProfileName(platform, id)).filter(Boolean);
  }

  return {
    hasFacebookIntegration,
    hasInstagramIntegration,
    hasTwitterIntegration,
    hasLinkedInIntegration,
    hasWordpressIntegration,
    hasTikTokIntegration,
    hasYouTubeIntegration,
    canPostToFacebook,
    canPostToInstagram,
    canPostToWordpress,
    canPostToTwitter,
    canPostToLinkedIn,
    canPostToTikTok,
    canPostToYouTube,
    getIntegrationProfileName,
    getProfileNames,
  };
}

export type IntegrationCheckReturn = ReturnType<typeof usePostIntegrationCheck>;
